/** KPI tiles for the period and the day-by-day trend (signups + revenue). */
import { useMemo } from "react";
import { Bar, CartesianGrid, ComposedChart, ResponsiveContainer, Tooltip as ReTooltip, XAxis, YAxis } from "recharts";
import { BadgeDollarSign, CreditCard, Eye, Hourglass, PiggyBank, Scale, UserPlus, Wallet } from "lucide-react";
import { useChartColors, useTheme } from "@/lib/theme";
import { Card, Delta, Stat, dayLabel, num, pct, usd } from "@/features/command/ui";
import type { SeriesPoint, SourceNumbers } from "../types";

const ZERO: SourceNumbers = { visits: 0, visitors: 0, signups: 0, trials: 0, paying: 0, revenue: 0, spend: 0 };

export function KpiTiles({ now: nowIn, prev: prevIn, totals, days }: { now: SourceNumbers | null; prev: SourceNumbers | null; totals: SourceNumbers; days: number }) {
  const now = nowIn ?? ZERO;
  const prev = prevIn ?? ZERO;
  const cpa = now.paying > 0 && now.spend > 0 ? now.spend / now.paying : null;
  const prevCpa = prev.paying > 0 && prev.spend > 0 ? prev.spend / prev.paying : null;
  const roas = now.spend > 0 ? now.revenue / now.spend : null;
  const vs = `vs the ${days} days before`;
  return (
    <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
      <Stat label="Visits" icon={Eye} value={num(now.visits)} now={now.visits} prev={prev.visits} sub={`${num(now.visitors)} visitors`} />
      <Stat
        label="Signups" icon={UserPlus} value={num(now.signups)} now={now.signups} prev={prev.signups}
        sub={totals.signups ? `${pct(now.signups / totals.signups)} of all signups` : vs}
      />
      <Stat label="Trials" icon={Hourglass} value={num(now.trials)} now={now.trials} prev={prev.trials} sub="Pro trials started" />
      <Stat label="Paying" icon={CreditCard} value={num(now.paying)} now={now.paying} prev={prev.paying} sub="Made a first payment" />
      <Stat label="Revenue" icon={BadgeDollarSign} tone="brand" big value={usd(now.revenue)} now={now.revenue} prev={prev.revenue} sub={totals.revenue ? `${pct(now.revenue / totals.revenue)} of all revenue` : vs} />
      <Stat
        label="Spend" icon={Wallet} value={usd(now.spend)}
        sub={<span className="flex items-center gap-1.5">Ads + other costs <Delta now={now.spend} prev={prev.spend} invert /></span>}
      />
      <Stat
        label="Cost per customer" icon={PiggyBank} value={cpa == null ? "–" : usd(cpa)}
        sub={cpa == null
          ? (now.paying ? "No spend recorded" : "No paying customers yet")
          : <span className="flex items-center gap-1.5">Spend ÷ paying {prevCpa != null && <Delta now={cpa} prev={prevCpa} invert />}</span>}
      />
      <Stat
        label="Revenue per $1" icon={Scale}
        tone={roas == null ? "default" : roas >= 1 ? "good" : "bad"}
        value={roas == null ? "–" : `$${roas.toFixed(2)}`}
        sub={roas == null ? "No spend recorded" : roas >= 1 ? "Making money" : "Spending more than it brings"}
      />
    </div>
  );
}

const REVENUE = { dark: "#34d399", light: "#059669" };

export function TrendCard({ series, days }: { series: SeriesPoint[]; days: number }) {
  const chart = useChartColors();
  const theme = useTheme();
  const rev = REVENUE[theme];
  const rows = useMemo(() => series.map((p) => ({ ...p, label: dayLabel(p.date) })), [series]);
  const signups = series.reduce((a, p) => a + p.signups, 0);
  const revenue = series.reduce((a, p) => a + p.revenue, 0);
  const empty = !signups && !revenue;
  const shown = Math.min(days, 90);

  return (
    <Card className="p-4">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="text-sm font-semibold text-zinc-100">Day by day</h2>
          <p className="text-xs text-zinc-500">Signups and money collected, last {shown} days</p>
        </div>
        <div className="flex items-center gap-4 text-xs">
          <span className="flex items-center gap-1.5 text-zinc-300">
            <span className="h-2.5 w-2.5 rounded-sm" style={{ background: chart.accent }} />
            {num(signups)} {signups === 1 ? "signup" : "signups"}
          </span>
          <span className="flex items-center gap-1.5 text-zinc-300">
            <span className="h-2.5 w-2.5 rounded-sm" style={{ background: rev }} />
            {usd(revenue)} revenue
          </span>
        </div>
      </div>
      {empty ? (
        <div className="flex h-24 items-center justify-center rounded-xl border border-dashed border-zinc-800 text-xs text-zinc-500">
          No signups or payments from here in this period yet
        </div>
      ) : (
        <ResponsiveContainer width="100%" height={200}>
          <ComposedChart data={rows} margin={{ top: 6, right: 4, left: -18, bottom: 0 }} barGap={1}>
            <CartesianGrid strokeDasharray="3 3" stroke={chart.grid} vertical={false} />
            <XAxis dataKey="label" tick={{ fontSize: 11, fill: chart.tick }} tickLine={false} axisLine={false} minTickGap={28} />
            <YAxis yAxisId="n" tick={{ fontSize: 11, fill: chart.tick }} tickLine={false} axisLine={false} allowDecimals={false} width={40} />
            <YAxis yAxisId="usd" orientation="right" tick={{ fontSize: 11, fill: chart.tick }} tickLine={false} axisLine={false} width={48} tickFormatter={(v: number) => usd(v, { compact: true })} />
            <ReTooltip
              cursor={{ fill: chart.grid, opacity: 0.5 }}
              contentStyle={{ background: chart.tooltipBg, border: `1px solid ${chart.tooltipBorder}`, color: chart.tooltipText, borderRadius: 10, fontSize: 12 }}
              labelStyle={{ color: chart.label, marginBottom: 4 }}
              formatter={(v, name) => (name === "Revenue" ? [usd(Number(v)), name] : [num(Number(v)), name])}
            />
            <Bar yAxisId="n" dataKey="signups" name="Signups" fill={chart.accent} radius={[3, 3, 0, 0]} maxBarSize={14} isAnimationActive={false} />
            <Bar yAxisId="usd" dataKey="revenue" name="Revenue" fill={rev} fillOpacity={0.85} radius={[3, 3, 0, 0]} maxBarSize={14} isAnimationActive={false} />
          </ComposedChart>
        </ResponsiveContainer>
      )}
    </Card>
  );
}
